import { Dialog, DialogProps } from '@mui/material';
import {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';

interface CustomDialogProps extends Omit<DialogProps, 'open' | 'children'> {
  disableClose?: boolean;
  onClose?: () => void;
}

export type OpenDialog = (
  content: ReactNode,
  props?: CustomDialogProps,
) => void;

interface DialogContext {
  openDialog: OpenDialog;
  closeDialog: () => void;
  dialogProps: CustomDialogProps;
  setDialogProps: Dispatch<SetStateAction<CustomDialogProps>>;
  dialogContent: ReactNode;
}

const DialogContext = createContext<DialogContext | undefined>(undefined);

export const DialogProvider: FC<CustomDialogProps> = ({
  children,
  disableClose,
  ...defaultProps
}) => {
  const [dialogContent, setDialogContent] = useState<ReactNode>(null);
  const [dialogProps, setDialogProps] = useState<CustomDialogProps>({});

  // In order to always have updated state in callbacks, we need to do this
  const propsRef = useRef<CustomDialogProps>({});
  // eslint-disable-next-line immutable/no-mutation
  propsRef.current = dialogProps;

  const openDialog: OpenDialog = useCallback((content, props) => {
    setDialogContent(content);
    setDialogProps(props || {});
  }, []);

  const closeDialog = useCallback(() => {
    defaultProps.onClose?.();
    propsRef.current.onClose?.();
    setDialogProps({});
    setDialogContent(null);
  }, [defaultProps]);

  return (
    <DialogContext.Provider
      value={{
        openDialog,
        closeDialog,
        dialogProps: { ...defaultProps, ...dialogProps },
        setDialogProps,
        dialogContent,
      }}
    >
      {children}
    </DialogContext.Provider>
  );
};

export const DialogDisplay: FC = () => {
  const context = useContext(DialogContext);

  if (!context) {
    throw new Error('You need to wrap DialogDisplay inside DialogProvider');
  }

  const { closeDialog, dialogProps, dialogContent } = context;
  const { disableClose, ...dialogPropsPassed } = dialogProps;

  return (
    <Dialog
      {...(dialogPropsPassed || {})}
      open={!!dialogContent}
      onClose={(e, reason) => {
        if (
          !disableClose ||
          (reason !== 'backdropClick' && reason !== 'escapeKeyDown')
        ) {
          closeDialog();
        }
      }}
    >
      {dialogContent}
    </Dialog>
  );
};

export const useDialog = (): Pick<
  DialogContext,
  'openDialog' | 'closeDialog' | 'setDialogProps'
> => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('You need to wrap useDialog inside DialogProvider');
  }
  const { openDialog, closeDialog, setDialogProps } = context;
  return { openDialog, closeDialog, setDialogProps };
};
